import { Checkbox } from 'antd';
import momentTz from 'moment-timezone';
import { useEffect, useState } from 'react';
import SvgIconError from '../@svg/SvgIconError';
import { StyledModal } from '../StyledModal';
import './index.scss';

/**
 * Cảnh báo khi gửi yêu cầu mà không chọn bảo hiểm (返送依頼 / 発送依頼), hiện ở màn 最終確認.
 * Ô 「今月はこのアラートを表示させない」 lưu tháng hiện tại (giờ JST) vào localStorage — dùng chung cho
 * cả 返送 lẫn 発送; sang tháng mới key không khớp nên popup hiện lại.
 */
const HIDDEN_MONTH_KEY = 'insuranceAlertHiddenMonth';

const currentMonthJst = () => momentTz().tz('Asia/Tokyo').format('YYYY-MM');

const isHiddenThisMonth = () => {
  try {
    return localStorage.getItem(HIDDEN_MONTH_KEY) === currentMonthJst();
  } catch {
    return false;
  }
};

const hideForThisMonth = () => {
  try {
    localStorage.setItem(HIDDEN_MONTH_KEY, currentMonthJst());
  } catch {
    // localStorage bị chặn thì chỉ mất tính năng ẩn, không chặn luồng gửi.
  }
};

export const shouldShowInsuranceAlert = (isInsurance: boolean | undefined) => !isInsurance && !isHiddenThisMonth();

type TModalInsuranceAlert = {
  open: boolean;
  type: 'return' | 'ship';
  /** 「保険なしで進める」— đóng popup, ở lại màn 最終確認. */
  onProceed: () => void;
  /** 「保険を再検討する」— quay về màn 料金情報 / 配送情報. */
  onReconsider: () => void;
};

export const ModalInsuranceAlert = ({ open, type, onProceed, onReconsider }: TModalInsuranceAlert) => {
  const [agreed, setAgreed] = useState(false);
  const [hideThisMonth, setHideThisMonth] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAgreed(false);
    setHideThisMonth(false);
  }, [open]);

  const handleProceed = () => {
    if (!agreed) return;
    if (hideThisMonth) hideForThisMonth();
    onProceed();
  };

  return (
    <StyledModal
      isOpen={open}
      onCancel={onReconsider}
      modalProps={{ footer: null, closable: false, maskClosable: false, className: 'modal-insurance-alert' }}
    >
      <div className="modal-insurance-alert__title">
        <SvgIconError />
        <span>
          保険が選択されていません。
          <br />
          このまま{type === 'return' ? '返送' : '発送'}依頼を継続しますか？
        </span>
      </div>

      <div className="modal-insurance-alert__rate">
        配送業者による紛失は<strong>1%程度</strong>の割合で発生しています
      </div>

      <div className="modal-insurance-alert__body">
        <p>
          保険に加入されない場合、配送中の紛失や破損についての補償交渉はご自身で行っていただきます。（
          <span className="modal-insurance-alert__warn">BayPackは補償や配送業者との補償交渉の代行を行いません。</span>
          <br />
          保険に加入された場合は、BayPackが補償申請を代行いたします。）
        </p>
        <p>
          配送業者の規定では最大$100の標準補償がございますが、補償金が支払われるまでには多くのやり取りと長い期間を要し、認められないケースも多くなっております。
        </p>
      </div>

      <div className="modal-insurance-alert__checks">
        <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)}>
          上記を了承し、保険なしで依頼します
        </Checkbox>
        <Checkbox checked={hideThisMonth} onChange={(e) => setHideThisMonth(e.target.checked)}>
          今月はこのアラートを表示させない
        </Checkbox>
        <p className="modal-insurance-alert__note">
          チェックを入れた場合は、今月のすべての返送・発送依頼に対して、
          <br />
          上記事項を了承したものとみなします。
        </p>
      </div>

      <div className="modal-insurance-alert__actions">
        <button type="button" className="modal-insurance-alert__proceed" disabled={!agreed} onClick={handleProceed}>
          保険なしで進める
        </button>
        <button type="button" className="modal-insurance-alert__reconsider" onClick={onReconsider}>
          保険を再検討する
        </button>
      </div>
    </StyledModal>
  );
};
